/**
 * A commission plan's status and Non-compliant chips (TMMPDM-0032; plans only since TMMPDM-0089, when
 * tmm-api 0154 retired every rate card's non-compliant flag) — words + chip + icon (STATE-001/002/003).
 * Both render the server's own fields: `status`, and `noncompliant` with `noncompliantSince`;
 * nothing here decides whether a plan can be used.
 */
import { Tooltip } from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { StatusChip } from '@/status';
import { formatDate } from '@/format';

const STATUS_FALLBACK: Record<string, string> = {
  DRAFT: 'Draft',
  ACTIVE: 'Active',
  CLOSED: 'Closed',
};

export function ResellerCardStatusChip({ status }: { status: string }) {
  const { t } = useCopy();
  const label = t(`rateCards.status.${status}`, STATUS_FALLBACK[status] ?? status);
  const icon =
    status === 'ACTIVE' ? (
      <CheckCircleOutlineIcon />
    ) : status === 'DRAFT' ? (
      <EditOutlinedIcon />
    ) : (
      <BlockIcon />
    );
  return <StatusChip status={status} icon={icon} label={label} />;
}

/** "Non-compliant since {{date}}" with its explanation as the tooltip; nothing when compliant. */
export function NoncompliantChip({
  card,
  plain = false,
  explanation,
}: {
  card: { noncompliant: boolean; noncompliantSince?: string };
  /** Inside a picker option: no tooltip and no tab stop of its own (the option is the control). */
  plain?: boolean;
  /** The tooltip: what the flag means for this record (TMMPDM-0041: a commission plan's). */
  explanation: string;
}) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  if (!card.noncompliant) return null;
  const label =
    card.noncompliantSince !== undefined
      ? t('resellerCards.noncompliant.since', 'Non-compliant since {{date}}', {
          date: formatDate(card.noncompliantSince, i18n.language),
        })
      : t('resellerCards.noncompliant.label', 'Non-compliant');
  if (plain) {
    return <StatusChip tone="warning" icon={<WarningAmberIcon />} label={label} />;
  }
  return (
    <Tooltip title={explanation} describeChild>
      <StatusChip tone="warning" icon={<WarningAmberIcon />} tabIndex={0} label={label} />
    </Tooltip>
  );
}
